import {EksisterendeDokument, FilopplastingAvhengigheter, Opplasting, Opplastinger, SekvensiellFilopplasting} from "felles-ng2/tjenester/filopplasting/SekvensiellFilopplasting";
import {SaksdokumentModell, SigneringStatus} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {DokumentOpplastingNyeversjonerDialogKomponent} from "../../dokument/modale/dokument-opplasting-nyeversjoner-dialog/dokument-opplasting-nyeversjoner-dialog";
import {firstValueFrom} from "rxjs";
import {DokumentOpplastingFeilDialogKomponent, FilMedUgyldigMimetype} from "../../dokument/modale/dokument-opplasting-feil-dialog/dokument-opplasting-feil-dialog";
import {computed, DestroyRef, Injectable, Signal} from "@angular/core";
import {filKanLeses} from "felles-ng2/støttefunksjoner/filer";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {standardFeilmeldingerForErrorRespons} from "felles-ng2/tjenester/ressurser/feilhåndtering";
import {i18n, I18nTekst, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {erIkkeTomListe, erTomListe, første} from "felles-ng2/støttefunksjoner/lister";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatDialog} from "@angular/material/dialog";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {Appkonfigurasjon} from "felles-ng2/tjenester/Appkonfigurasjon";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {Dialog} from "felles-ng2/tjenester/Dialog";
import {Feilhåndteringsstrategi} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Varighet} from "felles/tjenester/MeldingTilBruker";
import {dialogRespons} from "felles-ng2/infrastruktur/material/matDialogHjelpere";
import {RegistrerDokumentArkfane, RegistrerSaksdokumentDialogKomponent} from "web-ng2/moduler/sak/modale/registrer-saksdokument-dialog/registrer-saksdokument-dialog";
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";

const debug = initDebugger('SaksdokumentOpplasting');

/** Tjeneste som håndterer sekvensiell opplasting av saksdokumenter. */
@Injectable()
export class SaksdokumentOpplastingFabrikk {
    constructor(private saksdokumentDatatjeneste: SaksdokumentDatatjeneste,
                private konfigurasjon: Appkonfigurasjon,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private dialog: Dialog,
                private oversettelser: Oversettelser,
                private matDialog: MatDialog,
                private destroyRef: DestroyRef) {
    }

    forSak(sak: AgendaelementModellDLM): SaksdokumentOpplasting {
        return new SaksdokumentOpplasting(this.saksdokumentDatatjeneste, this.konfigurasjon, this.meldingTilBruker, this.dialog, this.oversettelser, this.matDialog, this.destroyRef)
          .forSak(sak);
    }
}

/** Håndterer opplasting av saksdokumenter for en spesifikk sak, benytter {@link SekvensiellFilopplasting} for å utføre opplastingen. */
export class SaksdokumentOpplasting {

    /** Saken {@link #opplastinger} gjelder for. Settes via {@link #forSak} som forventes kalt før noen andre metoder kalles  */
    private sak!: AgendaelementModellDLM;
    /** Håndterer sekvensiell opplasting av dokumenter */
    public sekvensiellFilopplasting: SekvensiellFilopplasting<SaksdokumentModell>;
    private opplastingerFeiletPgaUgyldigMimetype: FilMedUgyldigMimetype[] = [];

    constructor(private saksdokumentDatatjeneste: SaksdokumentDatatjeneste,
                private konfigurasjon: Appkonfigurasjon,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private dialog: Dialog,
                private oversettelser: Oversettelser,
                private matDialog: MatDialog,
                private destroyRef: DestroyRef) {
        this.sekvensiellFilopplasting = SekvensiellFilopplasting.tom<SaksdokumentModell>(this.filopplastingAvhengigheter());
    }

    forSak(sak: AgendaelementModellDLM): SaksdokumentOpplasting {
        this.sak = sak;
        return this;
    }

    /** Laster opp angitte filer til sak som nye saksdokumenter,
     * presenterer dialog dersom fil allerede er registrert som saksdokument og lar bruker velge om fil skal registreres som ny versjon. */
    async lastOppSaksdokumenter(filer: File[]): Promise<void> {
        const opplastinger = await this.sekvensiellFilopplasting.leggTilFiler(filer);

        //vis info om eventuelle 'nye versjoner' i modal, fortsett så opplasting dersom bruker velger dette
        if (opplastinger.validering.nyeVersjoner.length > 0) {
            const dialog = DokumentOpplastingNyeversjonerDialogKomponent.vis(this.matDialog, opplastinger.validering.nyeVersjoner);
            const fortsett = await firstValueFrom(dialog.afterClosed());
            if (!fortsett) {
                opplastinger.fjern();
                return;
            }
        }

        await this.utførOpplasting(opplastinger);
    }

    /** Laster opp angitt fil som ny versjon av angitt saksdokument */
    async registrerNyDokumentversjon(saksdokument: SaksdokumentModell, fil: File): Promise<void> {
      console.log('#NY VERR');
        const opplastinger = await this.sekvensiellFilopplasting.leggTilNyDokumentversjon(fil, this.somEksisterendeDokument(saksdokument));
        await this.utførOpplasting(opplastinger);
    }

    get harIkkeFerdigeOpplastinger(): Signal<boolean> {
        return computed(() => erIkkeTomListe(this.ikkeFerdigeOpplastinger()));
    }

    get ikkeFerdigeOpplastinger(): Signal<Opplasting<SaksdokumentModell>[]> {
        return this.sekvensiellFilopplasting.ikkeFerdigeSomSignal;
    }

    // private hjelpemetoder

    private somEksisterendeDokument(saksdokument: SaksdokumentModell): EksisterendeDokument<SaksdokumentModell> {
        return {
            dokument: saksdokument,
            erSendtTilSignering: saksdokument.signeringStatus != SigneringStatus.ikkeAktivert
        };
    }

    private async utførOpplasting(opplastinger: Opplastinger<SaksdokumentModell>) {
        //dersom bruker har valgt et Google-docs dokument gir vi bruker anledning til å åpne 'registrer saksdokument' dialogen med skytjenester-arkfanen aktivert
        if (opplastinger.validering.googleDocsFiler.length > 0) {
            return this.håndterGoogleDocsFilerValgt();
        }

        //vis eventuelle valideringsfeil i modal, avbryt så opplasting
        if (opplastinger.validering.harFeil) {
            const dialog = DokumentOpplastingFeilDialogKomponent.visValideringsfeil(this.matDialog, opplastinger.validering);
            await firstValueFrom(dialog.afterClosed());
            opplastinger.fjern();
            return;
            //kunne ha latt bruker velge å laste opp de filene som er 'ok':
        }

        //kun start opplasting dersom ikke allerede startet
        if (this.sekvensiellFilopplasting.opplastingPågårNå) return;
        this.sekvensiellFilopplasting.start()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
              next: httpHendelse => debug('opplasting:hendelse', httpHendelse),
              complete: () => {
                  debug('opplasting:ferdig')
                  this.visEventuellOpplastingerFeiletPgaUgyldigMimetype();
              }
          });
    }

    private async håndterGoogleDocsFilerValgt() {
        const åpneFilvelger = await this.dialog.advarsel({
            tittel: this.oversettelser.oversett(i18n('Opplasting av dokumenter fra Google')),
            innhold: this.oversettelser.oversett(i18n('Du har prøvd å dra-og-slippe et Google-docs dokument inn i saken. Google-docs dokumenter kan legges til saken ved å velge FILVELGER og deretter "Google Drive"')),
            okKnapp: this.oversettelser.oversett(i18n('Åpne filvelger')),
            avbrytKnapp: this.oversettelser.oversett(i18n('Avbryt'))
        });
        if (!åpneFilvelger) return;

        //åpner registrer saksdokument modal
        const respons = await dialogRespons(RegistrerSaksdokumentDialogKomponent.vis(this.matDialog, this.sak, RegistrerDokumentArkfane.skytjenester));
        if (erNil(respons) || respons.avbrutt || erTomListe(respons.filerTilOpplasting)) return;
        await this.lastOppSaksdokumenter(respons.filerTilOpplasting);
        return;
    }

    private visEventuellOpplastingerFeiletPgaUgyldigMimetype() {
        if (erTomListe(this.opplastingerFeiletPgaUgyldigMimetype)) return;
        DokumentOpplastingFeilDialogKomponent.visOpplastingsfeilUgyldigMimetype(this.matDialog, this.opplastingerFeiletPgaUgyldigMimetype);
        this.opplastingerFeiletPgaUgyldigMimetype = [];
    }

    private informerBrukerOmOpplastingAvbruttAvZscalar(filnavn: string) {
        return this.dialog.advarsel({
            tittel: this.oversettelser.oversett(i18n('Feil under opplasting av {filnavn}'), {filnavn: filnavn}),
            innhold: this.oversettelser.oversett(i18n('Din organisasjon benytter et system (Zscaler) for å overvåke/begrense informasjonsdeling. Kontakt din IT-ansvarlig for informasjon om opplasting av filer.')),
            droppAvbrytKnapp: true
        });
    }

    private filopplastingAvhengigheter(): FilopplastingAvhengigheter<SaksdokumentModell> {
        return {
            maksFilstørrelse: this.konfigurasjon.maksFilopplastingStørrelse,
            filKanLeses: fil => filKanLeses(fil),
            vedOpplastingFeilet: (feil, opplasting) => {
                if (erIkkeNil(feil.ugyldigMimetype)) {
                    this.opplastingerFeiletPgaUgyldigMimetype.push({filnavn: opplasting.fil.name, mimetype: feil.ugyldigMimetype});
                    return;
                }
                if (feil.erZscalarFeil) {
                    this.informerBrukerOmOpplastingAvbruttAvZscalar(opplasting.fil.name)
                    return;
                }

                let feilmelding = erIkkeNil(feil.httpFeil) ? standardFeilmeldingerForErrorRespons(feil.httpFeil) : null;
                if (erNil(feilmelding)) feilmelding = new I18nTekst(i18n('Opplasting av {fil} feilet'), {'fil': opplasting.fil.name})
                this.meldingTilBruker.feilmelding(feilmelding, Varighet.timeout, null, false /* ikke skriv til log */);
            },
            registrerNy: fil => this.saksdokumentDatatjeneste.registrerSaksdokument(this.sak.id, fil, Feilhåndteringsstrategi.propagerSomError),
            registrerNyVersjon: (fil, eksisterende) => this.saksdokumentDatatjeneste.registrerSaksdokumentVersjon(this.sak.id, eksisterende.dokumentId, fil, Feilhåndteringsstrategi.propagerSomError),
            eksisterende: navn => første(this.saksdokumentDatatjeneste.finnTilknyttetSakFraLokallagerSomListe(this.sak.id)
              .filter(eksisterende => eksisterende.filnavn == navn || eksisterende.pdfFilnavn == navn)
              .map(eksisterende => this.somEksisterendeDokument(eksisterende)))
        }
    }

}

